import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

import problemStatementService from '../../../services/problemStatementService';

const ActivityFeed = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllModal, setShowAllModal] = useState(false);
  const [allActivities, setAllActivities] = useState([]);

  const fetchActivities = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Step 1: Get approved problem statements with assigned teams
      const problemStatements = await problemStatementService?.getUserProblemStatements(user?.id);
      const approvedProblems = problemStatements?.filter(ps => ps?.status === 'approved' && ps?.assignedTeamId);
      const assignedTeamIds = approvedProblems?.map(ps => ps?.assignedTeamId)?.filter(Boolean);

      if (assignedTeamIds?.length === 0) {
        setActivities([]);
        setAllActivities([]);
        setLoading(false);
        return;
      }

      // Step 2: Fetch deliverable uploads from assigned teams
      const { data: uploads, error: uploadsError } = await supabase
        ?.from('team_deliverable_uploads')
        ?.select(`
          id,
          file_name,
          created_at,
          team_id,
          teams!inner (
            team_name,
            team_number
          ),
          user_profiles!team_deliverable_uploads_uploaded_by_fkey (
            full_name
          )
        `)
        ?.in('team_id', assignedTeamIds)
        ?.order('created_at', { ascending: false });

      if (uploadsError) throw uploadsError;

      // Step 3: Fetch stage completions (team_deliverables with status = completed)
      const { data: completions, error: completionsError } = await supabase
        ?.from('team_deliverables')
        ?.select(`
          id,
          deliverable_name,
          updated_at,
          team_id,
          teams!inner (
            team_name,
            team_number
          )
        `)
        ?.in('team_id', assignedTeamIds)
        ?.eq('status', 'completed')
        ?.order('updated_at', { ascending: false });

      if (completionsError) throw completionsError;

      // Combine and format activities
      const uploadActivities = uploads?.map(upload => ({
        id: `upload-${upload?.id}`,
        type: 'upload',
        title: `File uploaded: ${upload?.file_name}`,
        description: `${upload?.user_profiles?.full_name} uploaded a file to ${upload?.teams?.team_name}`,
        timestamp: new Date(upload?.created_at),
        teamName: upload?.teams?.team_name,
        teamNumber: upload?.teams?.team_number,
        icon: 'Upload',
        color: 'primary'
      })) || [];

      const completionActivities = completions?.map(completion => ({
        id: `completion-${completion?.id}`,
        type: 'completion',
        title: `Stage completed: ${completion?.deliverable_name}`,
        description: `${completion?.teams?.team_name} completed ${completion?.deliverable_name}`,
        timestamp: new Date(completion?.updated_at),
        teamName: completion?.teams?.team_name,
        teamNumber: completion?.teams?.team_number,
        icon: 'CheckCircle',
        color: 'success'
      })) || [];

      // Merge and sort by timestamp
      const allActivitiesSorted = [...uploadActivities, ...completionActivities]
        ?.sort((a, b) => b?.timestamp - a?.timestamp);

      // Store all activities for modal
      setAllActivities(allActivitiesSorted);
      
      // Limit to 10 most recent for main display
      setActivities(allActivitiesSorted?.slice(0, 10));
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError('Failed to load recent activities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [user?.id]);

  const getColorClasses = (color) => {
    const colors = {
      primary: 'bg-primary/10 text-primary',
      success: 'bg-success/10 text-success',
      warning: 'bg-warning/10 text-warning',
      error: 'bg-error/10 text-error'
    };
    return colors?.[color] || colors?.primary;
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const handleActivityAction = (activity) => {
    switch (activity?.type) {
      case 'deliverable_submitted':
        // Navigate to deliverables section or specific deliverable
        break;
      case 'message_received': navigate('/communication-hub');
        break;
      case 'deadline_approaching':
        // Navigate to team progress or send reminder
        break;
      default:
        break;
    }
  };

  const ActivityList = ({ activities, showTeamBadge = false }) => (
    <div className="space-y-3 sm:space-y-4">
      {activities?.map((activity) => (
        <div key={activity?.id} className="flex items-start space-x-3 sm:space-x-4 group">
          <div className={`p-2 rounded-lg ${getColorClasses(activity?.color)} flex-shrink-0`}>
            <Icon name={activity?.icon} size={16} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-foreground">{activity?.title}</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">{activity?.description}</p>
                {showTeamBadge && activity?.teamName && (
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-muted rounded-md">
                    {activity?.teamName}
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {formatTimeAgo(activity?.timestamp)}
              </span>
            </div>
            
            {activity?.actionable && (
              <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => handleActivityAction(activity)}
                  className="text-xs"
                >
                  View Details
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="bg-card border border-border rounded-lg shadow-soft">
        <div className="p-4 sm:p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-foreground">Recent Activity</h3>
            <Button
              variant="ghost"
              size="sm"
              iconName="RefreshCw"
              onClick={() => fetchActivities()}
            />
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="space-y-3 sm:space-y-4">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="animate-pulse flex items-start space-x-3 sm:space-x-4">
                  <div className="w-8 h-8 bg-muted rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : activities?.length > 0 ? (
            <ActivityList activities={activities} />
          ) : (
            <div className="text-center py-6 sm:py-8">
              <Icon name="Activity" size={40} className="sm:w-12 sm:h-12 mx-auto text-muted-foreground mb-3 sm:mb-4" />
              <p className="text-xs sm:text-sm text-muted-foreground">No recent activity from assigned teams.</p>
            </div>
          )}

          {activities?.length > 0 && (
            <div className="mt-4 sm:mt-6 pt-4 border-t border-border">
              <Button
                variant="ghost"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => setShowAllModal(true)}
                className="text-xs sm:text-sm"
              >
                View All Activity
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* View All Activity Modal */}
      {showAllModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-foreground">All Activity</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {allActivities?.length} total activities from assigned teams
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setShowAllModal(false)}
              />
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {allActivities?.length > 0 ? (
                <ActivityList activities={allActivities} showTeamBadge={true} />
              ) : (
                <div className="text-center py-8">
                  <Icon name="Activity" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground">No activities to display</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-6 border-t border-border">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowAllModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActivityFeed;